import React, { useState } from 'react';
import { Aperture } from 'lucide-react';
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const ScreenshotCapture = ({ onScreenshotComplete }) => {
    const [isCapturing, setIsCapturing] = useState(false);

    const handleCaptureScreenshot = async () => {
        try {
            setIsCapturing(true);
            
            // Check if getDisplayMedia is supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
                toast.error('Your browser does not support screen capture. Please use Chrome, Firefox, or Edge.');
                setIsCapturing(false);
                return;
            }

            // Request screen capture (video track only, no audio)
            const stream = await navigator.mediaDevices.getDisplayMedia({ 
                video: {
                    cursor: "always",
                    displaySurface: "monitor"
                },
                audio: false
            });

            // Get the video track
            const track = stream.getVideoTracks()[0];
            
            // Create an ImageCapture object
            const imageCapture = new ImageCapture(track);
            
            // Capture a frame
            const bitmap = await imageCapture.grabFrame();
            
            // Stop the stream immediately after capturing
            stream.getTracks().forEach(track => track.stop());
            
            // Convert bitmap to canvas and pass canvas for quality adjustment
            const canvas = document.createElement('canvas');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(bitmap, 0, 0);
            
            // Pass canvas so quality can be adjusted in preview
            onScreenshotComplete(canvas);
            toast.success('Screenshot captured!');
            setIsCapturing(false);

        } catch (err) {
            console.error('Screenshot capture error:', err);
            setIsCapturing(false);
            
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                toast.error('Screen capture permission denied. Please allow access and try again.');
            } else if (err.name === 'NotSupportedError') {
                toast.error('Screen capture is not supported in your browser.');
            } else if (err.name === 'AbortError') {
                // User cancelled the screen selection dialog - don't show error
                console.log('Screenshot capture cancelled by user');
            } else {
                toast.error('Unable to capture screenshot: ' + err.message);
            }
        }
    };

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCaptureScreenshot}
                    disabled={isCapturing}
                    className={cn(isCapturing && "opacity-50")}
                >
                    <Aperture className="h-5 w-5" />
                </Button>
            </TooltipTrigger>
            <TooltipContent>Capture Screenshot</TooltipContent>
        </Tooltip>
    );
};

export default ScreenshotCapture;

